import React from "react";
import { useDragOperation } from "@dnd-kit/react";

import {
  LockPosition,
  RouteBuilderProperty,
} from "../../../../../../../types/routeBuilder";
import {
  UNASSIGNED_ROUTE_ID,
  useRouteBuilder,
} from "../../../context/RouteBuilderContext";
import { getLockControlPosition } from "../../../lib/lockUtils";
import { getStopDropPreviewIndex } from "../../../lib/stopDropPreview";
import StopCard from "./StopCard";
import StopDropSlot from "./StopDropSlot";

/** Routes and the saved Unassigned list share the same insertion gaps. */
export function useStopSequence(
  routeId: string,
  stops: Array<{
    propertyVisitId: string;
    propertyId: string;
    lock?: LockPosition;
  }>,
  colorClass: string,
): React.ReactNode[] {
  const {
    getProperty,
    selectedPropertyVisitId,
    setSelectedPropertyVisitId,
    focusPropertyOnMap,
    setLock,
    removePropertyVisit,
  } = useRouteBuilder();

  const { source, target } = useDragOperation();
  const isUnassigned = routeId === UNASSIGNED_ROUTE_ID;

  const properties = stops.map((stop) => {
    const property = getProperty(stop.propertyId);
    return property
      ? {
          ...property,
          lock: isUnassigned
            ? null
            : stop.lock === undefined
              ? property.lock ?? null
              : stop.lock,
        }
      : undefined;
  });
  const locks = stops.map((stop, index) =>
    stop.lock !== undefined ? stop.lock : properties[index]?.lock ?? null,
  );
  const previewIndex =
    source?.type === "stop" && (isUnassigned || !source.data.lock)
      ? getStopDropPreviewIndex(
          routeId,
          stops.map((stop, index) => ({ ...stop, lock: locks[index] })),
          source.data.propertyVisitId,
          target,
        )
      : null;

  const nodes: React.ReactNode[] = [
    <StopDropSlot
      key={`${routeId}-slot-0`}
      routeId={routeId}
      index={0}
      active={previewIndex === 0}
    />,
  ];

  stops.forEach((stop, index) => {
    const property = properties[index] as RouteBuilderProperty | undefined;
    if (!property) return;
    nodes.push(
      <StopCard
        key={stop.propertyVisitId}
        propertyVisitId={stop.propertyVisitId}
        property={property}
        index={index}
        routeId={routeId}
        colorClass={colorClass}
        lockControlPosition={
          isUnassigned ? null : getLockControlPosition(locks, index)
        }
        highlight={selectedPropertyVisitId === stop.propertyVisitId}
        onOpen={() => {
          setSelectedPropertyVisitId(stop.propertyVisitId);
          focusPropertyOnMap(stop.propertyVisitId);
        }}
        onLockChange={(lock) => setLock(stop.propertyVisitId, lock)}
        onRemove={() => removePropertyVisit(stop.propertyVisitId)}
      />,
    );
    nodes.push(
      <StopDropSlot
        key={`${routeId}-slot-${index + 1}`}
        routeId={routeId}
        index={index + 1}
        active={previewIndex === index + 1}
      />,
    );
  });

  return nodes;
}
